import Link from 'next/link';
import {
  useCart,
  useCartTotals,
  useCartCheckout,
  useCartCount,
  useCartRemoveItem,
  useCartUpdateItem,
  useCountry,
  useSettings,
} from '@backpackjs/storefront';
import { useState, useEffect, useRef } from 'react';

import { useCartDiscountPrices } from '@hooks/useCartDiscountPrices';

import styles from './Cart.module.css';
import CartEmpty from './CartEmpty';
import CartItem from './CartItem';
import { GWPHandler } from './GWPHandler';
import { formatMoney } from '../../utilities/Helper';
import CartBundleItem from './CartBundleItem';

export function Cart() {
  const siteSettings = useSettings();
  const cart = useCart();
  const cartCount = useCartCount();
  const cartTotals = useCartTotals();
  const { discountAmount } = useCartDiscountPrices();
  const { cartCheckout } = useCartCheckout();
  const { cartRemoveItem } = useCartRemoveItem();
  const { cartUpdateItem } = useCartUpdateItem();
  let lines = [...(cart?.lines || [])]?.sort((a, b) => {
    const isFreeA = a.attributes.some((attr) =>
      ['_gwp', '_sample'].includes(attr.key)
    );
    const isFreeB = b.attributes.some((attr) =>
      ['_gwp', '_sample'].includes(attr.key)
    );
    return isFreeA === isFreeB ? 0 : isFreeA ? 1 : -1;
  });

  const hasBundleItems = lines.some((line) =>
    line.attributes.find((attr) => attr.key === '_bundleId')
  );

  if (hasBundleItems) {
    // Morphs the lines to identify bundle items

    lines = lines.map((line, index) => {
      const newLine = { ...line };

      if (line.attributes.find((attr) => attr.key === '_bundleId')) {
        const bundleItemsCount = newLine.attributes.find(
          (attr) => attr.key === '_bundleCount'
        ).value;

        newLine.isBundle = true;
        newLine.bundleItems = cart?.lines.filter((lineItem) => {
          return lineItem.attributes.some((attr) => attr.key === '_bundleId');
        });
      }

      return newLine;
    });
  }

  const cartSummary = useRef(null);
  const [summarySticky, setSummarySticky] = useState(false);

  const { country } = useCountry();
  const countryCode = country?.isoCode || 'US';
  const isUs = countryCode === 'US';
  const currencySymbol = country?.currency?.symbol || '$';

  const buildCartItems = () => {
    let skipItem = 0;
    const newLines = lines.map((item, index) => {
      if (skipItem > 0) {
        skipItem -= 1;
        return null;
      }

      if (item.isBundle) {
        skipItem = item.bundleItems.length - 1;
        return <CartBundleItem key={`lineItem-${index}`} item={item} />;
      }

      return (
        <CartItem
          key={`lineItem-${index}`}
          item={item}
          cartRemoveItem={cartRemoveItem}
          cartUpdateItem={cartUpdateItem}
          isUs={isUs}
        />
      );
    });

    return newLines;
  };

  useEffect(() => {
    const handleScroll = (evt) => {
      if (cartSummary.current) {
        const bounds = cartSummary.current.getBoundingClientRect();
        const viewHeight =
          window.innerHeight ||
          document.documentElement.clientHeight ||
          document.body.clientHeight;

        const { scrollY } = window;
        setSummarySticky(bounds.top >= viewHeight);
        window.lastSpot = scrollY;
      }
    };

    window.scrollTo(0, 1);
    if (!window.lastSpot) window.lastSpot = window.scrollY;
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const compareAtPriceTotal = cart?.lines?.reduce((accumulator, cartItem) => {
    let compareAmount =
      parseFloat(
        !!cartItem?.variant?.compareAtPriceV2?.amount &&
          cartItem?.variant?.compareAtPriceV2?.amount !== '0.0'
          ? cartItem?.variant?.compareAtPriceV2?.amount
          : cartItem?.variant?.priceV2.amount
      ) * cartItem.quantity;
    if (cartItem?.sellingPlanAllocation && compareAmount == 0) {
      compareAmount =
        parseFloat(cartItem?.variant?.priceV2?.amount) * cartItem.quantity;
    }

    return accumulator += compareAmount;
  }, 0);

  const discountHighlight =
    siteSettings?.discountHighlight?.enableDiscountHighlight === true &&
    siteSettings?.discountHighlight?.cartDiscountMessage !== '' &&
    compareAtPriceTotal - cartTotals?.subtotal + discountAmount > 0;

  return (
    <>
      {cartCount > 0 ? (
        <div className={styles.cart}>
          <div className="px-4 xl:px-0">
            <Link href="/" passHref>
              <a className="focus-link inline-block text-sm">
                continue shopping
              </a>
            </Link>
            <h1 className="h2 my-4 xl:mt-5 xl:mb-6">Your Cart</h1>
          </div>
          <div className={`${styles.cartBody}`}>
            <div className={`${styles.cartItems} pr-0 xl:pr-8 xl:pr-20`}>
              <div className="hidden items-center justify-between border-t border-b border-black py-4 text-center font-hafferBold text-h6 xl:flex">
                <div className="flex-grow text-left">Product</div>
                <div className="w-28 shrink-0">Qty</div>
                <div className="w-28 shrink-0">Total</div>
                <div className="w-20 shrink-0" />
              </div>
              {buildCartItems()}
              <GWPHandler gwpItems={[]} cartLines={lines} cartView={true} />
            </div>
            <div className="w-full max-w-full flex-shrink-0 xl:w-[375px]">
              <div
                className={`${styles.cartSummary} text-sm`}
                ref={cartSummary}
              >
                <p className="m-0 border-b border-black pb-3 text-center font-hafferBold text-sm">
                  Order summary ({cartCount} items){' '}
                </p>
                {discountHighlight && (
                  <>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="pr-4">Before Savings</span>
                      <div>
                        {formatMoney(compareAtPriceTotal.toFixed(2))?.replace(
                          '$',
                          currencySymbol
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="pr-4">
                        {siteSettings?.discountHighlight?.cartDiscountMessage}
                      </span>
                      <div className="text-primary-2">
                        - {formatMoney((compareAtPriceTotal -
                          cartTotals?.subtotal +
                          discountAmount).toFixed(2)).replace('$', currencySymbol)}
                      </div>
                    </div>
                  </>
                )}
                <div className="mt-4 flex items-center justify-between">
                  <span className="pr-4">Subtotal</span>
                  <span>
                    {formatMoney((cartTotals?.subtotal - discountAmount || 0).toFixed(2)).replace('$', currencySymbol)}
                  </span>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="pr-4">Shipping*</span>
                  <span>TBD</span>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="pr-4">Taxes*</span>
                  <span>TBD</span>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-black pt-3 font-hafferBold">
                  <span className="pr-4">Order Total</span>
                  <span>
                    {formatMoney(
                      (cartTotals?.subtotal - discountAmount).toFixed(2) || 0
                    ).replace('$', currencySymbol)}
                  </span>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    className="button button--checkout w-full"
                    onClick={cartCheckout}
                  >
                    checkout
                  </button>
                </div>
                <div className="whitespace-normal pt-4 text-center xl:pt-5">
                  <span className="text-xs">
                    *Shipping & Taxes Calculated at Checkout
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`${styles.cartSummarySticky} ${
              summarySticky ? styles['is-active'] : ''
            } text-sm`}
          >
            <div className="flex items-center justify-between font-hafferBold">
              <span className="pr-4">Subtotal</span>
              <span>
                {`${currencySymbol}${(
                  cartTotals?.subtotal - discountAmount || 0
                )
                  .toFixed(2)
                  .replace('.00', '')}`}
              </span>
            </div>
            <div className="mt-2">
              <button
                type="button"
                className="button button--checkout w-full"
                onClick={cartCheckout}
              >
                checkout
              </button>
            </div>
            <div className="whitespace-normal pt-2 text-center">
              <span className="text-xs">
                Any discounts, shipping & taxes calculated at checkout
              </span>
            </div>
          </div>
        </div>
      ) : null}
      {cartCount === 0 ? <CartEmpty /> : null}
    </>
  );
}
